import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { FileUploader } from "@/components/FileUploader";
import { AccountCard } from "@/components/AccountCard";
import { BiometricToggle } from "@/components/BiometricToggle";
import { storage } from "@/lib/storage";
import { filesApi } from "@/lib/files";
import { Store, Image as ImageIcon, FileText, ShieldCheck, Paperclip, Trash2 } from "lucide-react";

const SECTIONS = [
  { category: "logo", icon: ImageIcon, title: "Merchant Logo", desc: "Your brand mark on receipts & QR codes" },
  { category: "product", icon: Store, title: "Product Catalog", desc: "Images for items you sell" },
  { category: "invoice", icon: FileText, title: "Invoices & Receipts", desc: "Upload PDF receipts to keep records" },
  { category: "kyc", icon: ShieldCheck, title: "KYC Documents", desc: "Private · for your records only" },
  { category: "attachment", icon: Paperclip, title: "Other Attachments", desc: "Bills, delivery proof, misc." },
];

export default function MerchantSettings() {
  const profile = storage.getProfile() || { id: "M000000", name: "Merchant" };
  const [logo, setLogo] = useState(null);

  const loadLogo = async () => {
    try {
      const { data } = await filesApi.list({ ownerId: profile.id, category: "logo" });
      setLogo(data.files?.[0] || null);
    } catch (e) {}
  };

  useEffect(() => {
    loadLogo();
    // eslint-disable-next-line
  }, []);

  const logoUrl = logo ? filesApi.downloadUrl(logo.id, profile.id) : null;

  const clearProfile = () => {
    if (window.confirm("Reset local profile and PIN? You'll need to set up again.")) {
      storage.clearAll();
      window.location.href = "/";
    }
  };

  return (
    <div className="page-shell" data-testid="merchant-settings-page">
      <Header title="Settings & Files" subtitle="Manage your merchant profile" />
      <AccountCard />
      <BiometricToggle />

      <div className="card-glass gold-glow rounded-2xl p-6 mb-6 flex items-center gap-4 fade-slide-up" data-testid="profile-card">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="w-16 h-16 rounded-2xl object-cover border gold-border"
            data-testid="profile-logo-preview"
          />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
            <Store size={28} className="text-[#E6B455]" />
          </div>
        )}
        <div>
          <div className="font-bold text-[#F7E6B4] text-lg">{profile.name}</div>
          <div className="text-xs text-[#F7E6B4]/55 font-mono">{profile.id}</div>
        </div>
      </div>

      <div className="space-y-5">
        {SECTIONS.map((s) => (
          <div key={s.category} className="fade-slide-up" data-testid={`section-${s.category}`}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} className="text-[#E6B455]" />
              <h3 className="text-sm font-semibold text-[#F7E6B4]">{s.title}</h3>
            </div>
            <p className="text-xs text-[#F7E6B4]/55 mb-3">{s.desc}</p>
            <FileUploader
              ownerId={profile.id}
              ownerRole="merchant"
              category={s.category}
              onUploaded={s.category === "logo" ? loadLogo : undefined}
            />
          </div>
        ))}
      </div>

      <button
        onClick={clearProfile}
        className="mt-10 mx-auto flex items-center gap-2 text-xs text-[#F7E6B4]/50 hover:text-red-400 transition"
        data-testid="reset-profile-btn"
      >
        <Trash2 size={12} /> Reset local profile & PIN
      </button>
    </div>
  );
}
